import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '@shared/entities/user';
import { Machine } from '@shared/entities/machine';
import { UserFormService } from '@shared/services/api/user-form.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { ThemeService } from 'src/app/services/theme.service';
import { CompanyGroupService } from '@shared/services/api/company-group.service';
import { CompanyGroup } from '@shared/entities/companyGroup';
import {
  first,
  of,
  tap,
  catchError,
  switchMap,
  debounceTime,
  OperatorFunction,
  distinctUntilChanged,
  finalize,
  Observable,
  map,
  shareReplay,
  startWith
} from 'rxjs';
import { Location } from '@angular/common';
import { SFContactService } from '@shared/services/api/sf-contact.service';
import { SFAccount } from '@shared/entities/sf-account';
import {
  SFEntity,
  getContactProperties,
  formatContactDisplay as formatContact,
  isSFCustomerPartner
} from '@shared/entities/salesforce-entity';
import { UserService } from '@shared/services/api/user.service';
import { ModalService } from '@shared/services/modal.service';
import { ModalConfirmUserStatusComponent } from '../modal-confirm-user-status/modal-confirm-user-status.component';
import { Roles, SelectOptionType } from '@shared/models/types';
import { SfContactDialogComponent } from '../sf-contact-dialog/sf-contact-dialog.component';
import { ValuesService } from '@shared/services/api/values.service';
import { CreateAdminModalComponent } from '../create-admin-modal/create-admin-modal.component';
import { UserWelcomeMessage } from '@shared/entities/userWelcomeMessage';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserFormComponent implements OnInit {
  environment = environment;
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);

  form!: FormGroup;
  _user: User = new User();
  private _userId = '';
  isEdit = false;
  isLight = true;
  isLoading = false;
  showSuccess = false;
  showError = false;
  error: any = null;
  errorMessage = '';

  availableSfContact: SFEntity[] = [];
  availableRoles: SelectOptionType[] = [];

  availablePermissions: SelectOptionType[] = [];

  isContactSelectionDisabled = true;

  saveSuccess = false;
  saveError = false;
  statusChangeSuccess = false;
  statusChangeError = false;
  isContactSearchLoading = false;

  selectedContact: SFEntity | null = null;

  isCurrentUserAdmin = false;
  isCurrentUserAdmin$: Observable<boolean>;
  isSubmitDisabled$!: Observable<boolean>;

  selectedAccountIds: string[] = [];

  constructor(
    private _formBuilder: FormBuilder,
    private _router: Router,
    private _route: ActivatedRoute,
    private _userFormService: UserFormService,
    private _userService: UserService,
    private _sfContactService: SFContactService,
    private _themeService: ThemeService,
    private _location: Location,
    private _modalService: ModalService,
    private _profileService: ProfileService,
    private _valuesService: ValuesService
  ) {
    this.isCurrentUserAdmin$ = this._profileService.role$.pipe(
      map((role) => role === Roles.ADMIN),
      shareReplay(1)
    );
  }

  companyGroups: CompanyGroup[] = [];
  private _companyGroupService = inject(CompanyGroupService);

  ngOnInit(): void {
    this._valuesService
      .getRoles()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        const values = res.values;
        if (Array.isArray(values)) {
          this.availableRoles = values
            .map((role: any) => {
              if (typeof role === 'string') {
                return {
                  id: role,
                  value: `USER_FORM.ROLES.${role}`
                };
              }
              const key = role.key || role.id;
              return {
                id: key,
                value: role.value || `USER_FORM.ROLES.${key}`
              };
            })
            .filter((role) => role.id !== 'SUPER_ADMIN' && role.id !== 'ADMIN');
        } else if (values && typeof values === 'object') {
          this.availableRoles = Object.keys(values)
            .map((key) => ({
              id: key,
              value: `USER_FORM.ROLES.${key}`
            }))
            .filter((role) => role.id !== 'SUPER_ADMIN' && role.id !== 'ADMIN');
        }
        this._cdr.markForCheck();
      });

    this.initializeForm();

    // Observable che si aggiorna ad ogni cambio del form — risolve il problema OnPush con isSubmitDisabled
    this.isSubmitDisabled$ = this.form.statusChanges.pipe(
      startWith(this.form.status),
      map(() => this.isSubmitDisabled()),
      shareReplay(1)
    );

    this._companyGroupService.refresh(1, 100).subscribe((paginated) => {
      this.companyGroups = paginated.data || [];
    });

    this._themeService.isLight$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((isLight) => {
      this.isLight = isLight;
      this._cdr.markForCheck();
    });

    this.hasAdminRole()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isAdmin) => {
        this.isCurrentUserAdmin = isAdmin;
        if (!isAdmin) {
          this.disableFormForNonAdmin();
        }
        this._cdr.markForCheck();
      });

    this._route.params
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((params) => {
          this._userId = params['id'] || '';
          this.isEdit = !!this._userId;
          this.updateFormValidation();
          if (this.isEdit) {
            this.form.get('role')?.disable();
            this.loadUserData();
          } else {
            this.form.get('role')?.enable();
            this.loadAllContacts();
          }
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this.form
      .get('sfContactId')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef), debounceTime(300))
      .subscribe((contactId) => {
        if (contactId && typeof contactId === 'string') {
          this.onContactChange(contactId);
        }
        this._cdr.markForCheck();
      });

    this.form
      .get('role')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((role) => {
        if (!this.isEdit) {
          this.clearFormOnRoleChange();
        }
        this.updateFormValidation();

        if (role !== Roles.PARTNER) {
          this.form.patchValue({ selectedAccounts: [] });
        }

        if (role === Roles.ADMIN) {
          const allPermissionsExceptFinalUsers = this.availablePermissions
            .filter((permission) => permission.id !== 'final-users')
            .map((permission) => permission.id.toString());
          this.form.patchValue({
            permissions: allPermissionsExceptFinalUsers
          });
        } else if (role === Roles.CUSTOMER || role === Roles.CUSTOMER_PARTNER) {
          // Set default permissions for customer roles
          const customerDefaultPermissions = ['home', 'repository', 'analytics', 'ticket', 'request', 'learning'];
          this.form.patchValue({
            permissions: customerDefaultPermissions
          });
        } else if (role === Roles.PARTNER) {
          // Set default permissions for partner role
          const partnerDefaultPermissions = ['repository', 'ticket', 'request', 'learning'];
          this.form.patchValue({
            permissions: partnerDefaultPermissions
          });
        }
        this._cdr.markForCheck();
      });

    this.form
      .get('selectedAccounts')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((accounts) => {
        const newIds = (accounts || []).map((a: SFAccount) => a.id);
        if (JSON.stringify(newIds) !== JSON.stringify(this.selectedAccountIds)) {
          this.selectedAccountIds = newIds;
        }
        this._cdr.markForCheck();
      });
  }

  private clearFormOnRoleChange(): void {
    const currentRole = this.form.get('role')?.value;

    this.selectedContact = null;

    this.form.patchValue({
      sfContactId: '',
      firstName: '',
      lastName: '',
      email: '',
      mobilePhone: '',
      password: '',
      permissions: [],
      selectedMachines: [],
      selectedAccounts: []
    });

    if (this.form.get('role')?.value !== currentRole) {
      this.form.get('role')?.setValue(currentRole, { emitEvent: false });
    }
  }

  loadUserData(): void {
    if (!this._userId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this._userFormService
      .findUser(this._userId)
      .pipe(
        switchMap((user) => user ? of(user) : this._userFormService.findByFilter(this._userId)),
        tap((user) => {
          if (user) {
            this._user = user;
            this.patchFormValues(user);
          }
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
        catchError((error) => {
          console.error('Error loading user data:', error);
          this.isLoading = false;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  private initializeForm(): void {
    this.form = this._formBuilder.group({
      id: [''],
      firstName: [{ value: '', disabled: true }],
      lastName: [{ value: '', disabled: true }],
      email: [{ value: '', disabled: true }],
      mobilePhone: [{ value: '', disabled: true }],
      createdDate: [{ value: '', disabled: true }],
      modifiedDate: [{ value: '', disabled: true }],
      password: [''],
      sfContactId: ['', Validators.required],
      role: ['', Validators.required],
      status: [''],
      permissions: [[]],
      selectedMachines: [[]],
      selectedAccounts: [[]],
      welcomeSubject: [''],
      templateMail: [''],
      templateLanguage: [''],
      companyGroupId: [''],
      canImpersonate: [false]
    });

    this.populateAvailablePermissions();
  }

  onAccountSelection(account: SFAccount, checked: boolean): void {
    if (!this.isCurrentUserAdmin) return;
    let selected = this.form.get('selectedAccounts')?.value || [];
    selected = checked
      ? selected.some((a: SFAccount) => a.id === account.id)
        ? selected
        : [...selected, account]
      : selected.filter((a: SFAccount) => a.id !== account.id);

    this.form.patchValue({ selectedAccounts: selected });
  }

  onAccountsLoaded(): void {
    // Handler for when accounts are loaded in the child component
  }

  onMachinesLoaded(): void {
    // Handler for when machines are loaded in the child component
  }

  getSelectedAccountIds(): string[] {
    const accounts = this.form.get('selectedAccounts')?.value || [];
    return accounts.map((a: SFAccount) => a.id);
  }

  private populateAvailablePermissions(): void {
    this.availablePermissions = [
      { id: 'home', value: 'SIDEBAR.HOME' },
      { id: 'repository', value: 'SIDEBAR.REPOSITORY' },
      { id: 'analytics', value: 'SIDEBAR.ANALYTICS' },
      { id: 'ticket', value: 'SIDEBAR.TICKET' },
      { id: 'request', value: 'SIDEBAR.REQUEST' },
      { id: 'learning', value: 'SIDEBAR.LEARNING' },
      { id: 'shopify', value: 'SIDEBAR.SHOPIFY' },
      { id: 'knowledge-base', value: 'SIDEBAR.KNOWLEDGE_BASE' }
    ];
  }

  private updateFormValidation() {
    const passwordControl = this.form.get('password');
    const templateLanguageControl = this.form.get('templateLanguage');

    if (!this.isEdit) {
      passwordControl?.setValidators([Validators.required, Validators.minLength(8)]);
      templateLanguageControl?.setValidators([Validators.required]);
    } else {
      passwordControl?.clearValidators();
      templateLanguageControl?.clearValidators();
    }
    passwordControl?.updateValueAndValidity();
    templateLanguageControl?.updateValueAndValidity();

    const selectedAccountsControl = this.form.get('selectedAccounts');
    if (this.isPartner()) {
      selectedAccountsControl?.setValidators([Validators.required]);
    } else {
      selectedAccountsControl?.clearValidators();
    }
    selectedAccountsControl?.updateValueAndValidity();
  }

  compareAccountsById(a: SFAccount, b: Machine): boolean {
    return a?.id === b?.id;
  }

  compareContactsById(a: SFEntity, b: SFEntity): boolean {
    return a?.id === b?.id;
  }

  private patchFormValues(user: User): void {
    this.form.patchValue({
      id: user.id || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      mobilePhone: user.mobilePhone || '',
      createdDate: user.createdDate || '',
      modifiedDate: user.modifiedDate || '',
      sfContactId: user.sfContactId || '',
      role: user.role || '',
      status: user.status,
      permissions: user.permissions || [],
      companyGroupId: user.companyGroupId || '',
      canImpersonate: user.canImpersonate ?? false
    });

    if (user.sfContactId) {
      this._sfContactService
        .searchContactById(user.sfContactId)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((contact) => {
          if (contact) {
            this.selectedContact = contact;
          }
          this._cdr.markForCheck();
        });
    }

    if (user.role === Roles.PARTNER && user.accounts) {
      this.form.patchValue({ selectedAccounts: user.accounts });
      this.updateFormValidation();
    }
  }

  onSubmit(): void {
    const role = this.form.get('role')?.value;
    if (role !== Roles.PARTNER) {
      const selectedAccountsControl = this.form.get('selectedAccounts');
      selectedAccountsControl?.clearValidators();
      selectedAccountsControl?.updateValueAndValidity();
    }

    if (this.form.valid || (this.isAdmin() && this.isFormValidForAdmin())) {
      this.processFormSubmission();
    } else {
      this.markAllFieldsAsTouched();
    }
  }

  private isFormValidForAdmin(): boolean {
    return Object.keys(this.form.controls)
      .filter((key) => key !== 'selectedAccounts')
      .every((key) => !this.form.get(key)?.invalid);
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.get(key)?.markAsTouched();
    });
  }

  private processFormSubmission(): void {
    this.isLoading = true;
    const formData = this.form.getRawValue();

    this._user.firstName = formData.firstName;
    this._user.lastName = formData.lastName;
    this._user.email = formData.email;
    this._user.mobilePhone = formData.mobilePhone;

    const machines = formData.selectedMachines || [];

    const userData: User = {
      ...this._user,
      sfContactId: formData.sfContactId,
      role: formData.role,
      status: formData.status || 'ACTIVE',
      permissions: formData.permissions,
      machines: machines,
      accounts: this.isPartner() ? formData.selectedAccounts || [] : [],
      companyGroupId: !this.isAdmin() ? formData.companyGroupId || '' : undefined,
      canImpersonate: !this.isAdmin() ? formData.canImpersonate ?? false : undefined
    };

    if (!this.isEdit) {
      if (formData.password) userData.password = formData.password;
      if (formData.templateMail || formData.welcomeSubject) {
        userData.templateMail = {
          subject: formData.welcomeSubject || '',
          message: formData.templateMail || ''
        } as UserWelcomeMessage;
      }
    }

    const saveAction = this.isEdit
      ? this._userFormService.editUser(userData, this._userId)
      : this._userFormService.saveUser(userData);

    saveAction
      .pipe(
        first(),
        tap((response: any) => this.handleSaveResponse(response)),
        catchError((err) => this.handleSaveError(err))
      )
      .subscribe();
  }

  private handleSaveResponse(response: any): void {
    this.isLoading = false;

    if (response && (response.error || response.validation)) {
      this.showError = true;
      this.saveError = true;
      this.saveSuccess = false;
      this.statusChangeError = false;
      this.statusChangeSuccess = false;
      this.error = response.error || null;
      this.errorMessage = this.extractErrorMessage(response);
      this._userService.refresh(1);
      this._cdr.markForCheck();
      return;
    }

    this.showSuccess = true;
    this.saveSuccess = true;
    this.statusChangeSuccess = false;
    this.saveError = false;
    this.statusChangeError = false;
    this.showError = false;
    this._userService.refreshFilters();
    this._cdr.markForCheck();
  }

  private handleSaveError(err: any): Observable<null> {
    this.showError = true;
    this.saveError = true;
    this.statusChangeError = false;
    this.saveSuccess = false;
    this.statusChangeSuccess = false;
    this.isLoading = false;
    this.errorMessage = this.extractErrorMessage(err?.error);
    this._cdr.markForCheck();
    return of(null);
  }

  onMachineSelection(machine: Machine, isChecked: boolean): void {
    if (!this.isCurrentUserAdmin) {
      return;
    }

    const currentMachines = this.form.get('selectedMachines')?.value || [];
    const updatedMachines = isChecked
      ? [...currentMachines, machine]
      : currentMachines.filter((m: Machine) => m.id !== machine.id);

    this.form.patchValue({ selectedMachines: updatedMachines });
  }

  onPermissionSelection(permissionId: string, isChecked: boolean): void {
    if (!this.isCurrentUserAdmin) return;

    const currentPermissions = this.form.get('permissions')?.value || [];
    const updatedPermissions = isChecked
      ? currentPermissions.includes(permissionId)
        ? currentPermissions
        : [...currentPermissions, permissionId]
      : currentPermissions.filter((p: string) => p !== permissionId);

    this.form.patchValue({ permissions: updatedPermissions });
  }

  resetForm(): void {
    if (this.isEdit) {
      this.patchFormValues(this._user);

      if (this._user.role === Roles.PARTNER && this._user.accounts) {
        this.form.patchValue({ selectedAccounts: this._user.accounts });
      }
    } else {
      this.form.reset({
        permissions: [],
        selectedMachines: [],
        selectedAccounts: []
      });
    }

    this.showSuccess = false;
    this.showError = false;
    this.saveSuccess = false;
    this.saveError = false;
    this.statusChangeSuccess = false;
    this.statusChangeError = false;
  }

  goBack(): void {
    this._userService.refreshFilters();
    this._location.back();
  }

  onDeactivateUser(): void {
    this.updateUserStatus('INACTIVE', false);
  }

  onActivateUser(): void {
    this.updateUserStatus('ACTIVE', true);
  }

  private updateUserStatus(status: 'ACTIVE' | 'INACTIVE', isActivation: boolean): void {
    const modalRef = this._modalService.open(ModalConfirmUserStatusComponent, {
      showClose: true,
      showBtns: true
    });

    if (isActivation) modalRef.componentInstance.isActivation = true;

    modalRef.result.then(
      (result) => {
        if (result === 'confirm') {
          this.isLoading = true;
          this.form.patchValue({ status });
          const formData = this.form.getRawValue();

          const userData: User = {
            ...this._user,
            sfContactId: formData.sfContactId,
            role: formData.role,
            status: status,
            permissions: formData.permissions,
            machines: formData.selectedMachines || [],
            accounts: formData.role === Roles.PARTNER ? formData.selectedAccounts : undefined
          };

          this._userFormService
            .editUser(userData, this._userId)
            .pipe(
              first(),
              tap((response: any) => {
                this.isLoading = false;
                if (response && (response.error || response.validation)) {
                  this.showError = true;
                  this.statusChangeError = true;
                  this.saveError = false;
                  this.statusChangeSuccess = false;
                  this.saveSuccess = false;
                  this.errorMessage = this.extractErrorMessage(response);
                  this._userService.refresh(1);
                  this._cdr.markForCheck();
                  return;
                }

                this.showSuccess = true;
                this.statusChangeSuccess = true;
                this.saveSuccess = false;
                this.statusChangeError = false;
                this.saveError = false;
                this._userService.refreshFilters();

                setTimeout(() => this._router.navigate(['/user']), 2000);
                this._cdr.markForCheck();
              }),
              catchError((error) => {
                this.showError = true;
                this.statusChangeError = true;
                this.saveError = false;
                this.statusChangeSuccess = false;
                this.saveSuccess = false;
                this.errorMessage = this.extractErrorMessage(error?.error);
                console.error(`Failed to ${isActivation ? 'activate' : 'deactivate'} user:`, error);
                this._cdr.markForCheck();
                return of(null);
              })
            )
            .subscribe();
        }
      },
      () => {
        /* noop */
      }
    );
  }

  onDeleteUser(): void {
    const modalRef = this._modalService.open(ModalConfirmUserStatusComponent, {
      showClose: true,
      showBtns: true
    });

    modalRef.componentInstance.isDeletion = true;
    modalRef.result.then((result) => {
      if (result === 'confirm') {
        this.isLoading = true;

        this._userFormService
          .deleteUser(this._userId)
          .pipe(
            first(),
            tap((response: any) => {
              this.isLoading = false;

              if (response && response.error) {
                this.showError = true;
                this.statusChangeError = true;
                this.saveError = false;
                this.statusChangeSuccess = false;
                this.saveSuccess = false;

                this.errorMessage = this.extractErrorMessage(response);
                this._cdr.markForCheck();
                return;
              }

              this.showSuccess = true;
              this.statusChangeSuccess = true;
              this.saveSuccess = false;
              this.statusChangeError = false;
              this.saveError = false;
              this._userService.refreshFilters();

              setTimeout(() => {
                this._router.navigate(['/user']);
              }, 2000);
              this._cdr.markForCheck();
            }),
            catchError((error) => {
              this.isLoading = false;
              this.showError = true;
              this.statusChangeError = true;
              this.saveError = false;
              this.statusChangeSuccess = false;
              this.saveSuccess = false;

              this.errorMessage = error?.error?.message || 'An error occurred while deleting the user';
              console.error('Failed to delete user:', error);
              this._cdr.markForCheck();
              return of(null);
            })
          )
          .subscribe();
      }
    });
  }

  onContactChange(contactId: string): void {
    if (contactId) {
      const selectedContact = this.availableSfContact.find((contact) => contact.id === contactId);
      if (selectedContact) {
        const properties = getContactProperties(selectedContact);

        this._user.firstName = properties.firstName;
        this._user.lastName = properties.lastName;
        this._user.email = properties.email;
        this._user.mobilePhone = properties.mobilePhone;

        this.form.patchValue({
          firstName: properties.firstName,
          lastName: properties.lastName,
          email: properties.email,
          mobilePhone: properties.mobilePhone
        });
      }
    }
  }

  private loadAllContacts(): void {
    this._sfContactService
      .searchContactExcludeActivated('')
      .pipe(first())
      .subscribe((contacts) => {
        this.availableSfContact = contacts;
        this._cdr.markForCheck();
      });
  }

  searchContacts: OperatorFunction<string, readonly SFEntity[]> = (text$: Observable<string>) => {
    return text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) => {
        if (!term || term.length < 2) {
          return of(this.availableSfContact);
        }

        return of(
          this.availableSfContact.filter((contact) => {
            const firstName = contact.firstName || '';
            const lastName = contact.lastName || '';
            const fullName = `${firstName} ${lastName}`.toLowerCase();
            const email = (contact.email || '').toLowerCase();
            const searchTerm = term.toLowerCase();

            return fullName.includes(searchTerm) || email.includes(searchTerm);
          })
        );
      })
    );
  };

  onSelectContact(event: any): void {
    if (event) {
      let contact: SFEntity | undefined;

      if (typeof event === 'object' && event.item) {
        contact = event.item;
      } else if (typeof event === 'object') {
        contact = event;
      } else {
        contact = this.availableSfContact.find((c) => c.id === event);
      }

      if (contact) {
        const properties = getContactProperties(contact);

        this.form.patchValue({
          sfContactId: properties.id,
          firstName: properties.firstName || '',
          lastName: properties.lastName || '',
          email: properties.email || '',
          mobilePhone: properties.mobilePhone || '',
          permissions: [],
          selectedMachines: [],
          selectedAccounts: []
        });
      }
    }
  }

  searchSfContacts = (searchTerm: string): Observable<any[]> => {
    this.isContactSearchLoading = true;

    return this._sfContactService.searchContactExcludeActivated(searchTerm).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      finalize(() => {
        this.isContactSearchLoading = false;
      }),
      catchError(() => {
        this.isContactSearchLoading = false;
        return of([]);
      })
    );
  };

  formatContactDisplay = (contact: SFEntity | null): string => {
    return formatContact(contact);
  };

  onContactSelected(contact: SFEntity | null): void {
    if (contact) {
      const properties = getContactProperties(contact);

      this.form.patchValue({
        firstName: properties.firstName || '',
        lastName: properties.lastName || '',
        email: properties.email || '',
        mobilePhone: properties.mobilePhone || ''
      });
    }
  }

  openSFContactDialog(): void {
    const modalRef = this._modalService.open(SfContactDialogComponent, {
      size: 'lg',
      backdrop: 'static'
    });

    modalRef.componentInstance.selectedContactId = this.form.get('sfContactId')?.value;
    modalRef.componentInstance.selectedRole = this.form.get('role')?.value;

    modalRef.result.then(
      (contact: SFEntity) => {
        if (contact) {
          if (isSFCustomerPartner(contact) && contact.contactId) {
            this._sfContactService
              .searchContactById(contact.contactId)
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe((sfContact) => {
                if (sfContact) {
                  this.selectedContact = sfContact;
                  const properties = getContactProperties(sfContact);

                  this.form.patchValue({
                    sfContactId: properties.id,
                    firstName: properties.firstName || '',
                    lastName: properties.lastName || '',
                    email: properties.email || '',
                    mobilePhone: properties.mobilePhone || '',
                    permissions: [],
                    selectedMachines: [],
                    selectedAccounts: []
                  });
                  this._cdr.markForCheck();
                }
              });
          } else {
            this.selectedContact = contact;
            const properties = getContactProperties(contact);

            this.form.patchValue({
              sfContactId: properties.id,
              firstName: properties.firstName || '',
              lastName: properties.lastName || '',
              email: properties.email || '',
              mobilePhone: properties.mobilePhone || '',
              permissions: [],
              selectedMachines: [],
              selectedAccounts: []
            });
            this._cdr.markForCheck();
          }
        }
      },
      () => {
        /* noop */
      }
    );
  }

  openCreateAdminModal(): void {
    const modalRef = this._modalService.open(
      CreateAdminModalComponent,
      {
        showClose: true,
        showBtns: true
      },
      {
        size: 'xl',
        backdrop: 'static',
        scrollable: true
      }
    );

    modalRef.result.then((result: any) => {
      if (result && typeof result === 'object' && result.success) {
        this.showSuccess = true;
        this.saveSuccess = true;
        this.saveError = false;
        this._userService.refreshFilters();

        setTimeout(() => {
          this.goBack();
        }, 2000);
        this._cdr.markForCheck();
      } else if (result && typeof result === 'object' && result.success === false) {
        this.showError = true;
        this.saveError = true;
        this.saveSuccess = false;
        this.error = result.error;
        this.errorMessage = this.extractErrorMessage(result.error);
        this._cdr.markForCheck();
      }
    });
  }

  isAdmin(): boolean {
    return this.form.get('role')?.value === Roles.ADMIN;
  }

  isPartner(): boolean {
    return this.form.get('role')?.value === Roles.PARTNER;
  }

  isCustomerPartner(): boolean {
    return this.form.get('role')?.value === Roles.CUSTOMER_PARTNER;
  }

  isCustomer(): boolean {
    return this.form.get('role')?.value === Roles.CUSTOMER;
  }

  hasAdminRole(): Observable<boolean> {
    return this._profileService.role$.pipe(map((role) => role === Roles.ADMIN));
  }

  private disableFormForNonAdmin(): void {
    this.form.get('firstName')?.disable();
    this.form.get('lastName')?.disable();
    this.form.get('email')?.disable();
    this.form.get('mobilePhone')?.disable();
    this.form.get('password')?.disable();
    this.form.get('sfContactId')?.disable();
    this.form.get('role')?.disable();
    this.form.get('status')?.disable();
    this.form.get('permissions')?.disable();
    this.form.get('selectedMachines')?.disable();
    this.form.get('selectedAccounts')?.disable();
  }

  private extractErrorMessage(response: any): string {
    if (!response) {
      return 'An unknown error occurred';
    }

    if (response.error.message) {
      return response.error.message;
    }

    if (response.error.validation && response.error.validation.fields && response.error.validation.fields.length > 0) {
      const validationErrors = response.error.validation.fields
        .map((field: any) => `${field.field}: ${field.error}`)
        .join('\n');

      return `Validation errors:\n${validationErrors}`;
    }

    if (response.error && typeof response.error === 'string') {
      return response.error;
    }

    if (this.saveError) {
      return 'USER_FORM.SAVE_ERROR';
    } else if (this.statusChangeError) {
      return 'USER_FORM.STATUS_CHANGE_ERROR';
    }

    return 'An unknown error occurred';
  }

  isSubmitDisabled(): boolean {
    if (this.form.get('sfContactId')?.invalid || this.form.get('role')?.invalid) {
      return true;
    }

    if (!this.isEdit && this.form.get('password')?.invalid) {
      return true;
    }

    if (!this.isEdit && this.form.get('templateLanguage')?.invalid) {
      return true;
    }

    const role = this.form.get('role')?.value;
    const permissions = this.form.get('permissions')?.value || [];
    const selectedAccounts = this.form.get('selectedAccounts')?.value || [];

    if (role === Roles.ADMIN) {
      return false;
    }

    if (role === Roles.PARTNER) {
      return selectedAccounts.length === 0 || permissions.length === 0;
    }

    return permissions.length === 0;
  }

  isDeleteDisabled(): boolean {
    return (
      !this.isCurrentUserAdmin ||
      !this.isEdit ||
      this.saveSuccess ||
      this.statusChangeSuccess ||
      this.saveError ||
      this.statusChangeError ||
      !environment.allowedDeleteUser
    );
  }
}
